/* eslint-disable jsx-a11y/media-has-caption */
import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Carousel, Popup, Button, Toast } from 'zarm';
import { load } from '@iyunbao/utils';

import { BaseWidgetProps } from '../utils/type';
import LockScroll from '../lockScroll';

type OptionsProps = Array<{
  type?: string;
  href: string;
  url?: string;
  alt?: string;
  videoUrl?: string;
  log?: string;
}>;

export interface CarouselProps extends BaseWidgetProps {
  options: OptionsProps;
  direction?: 'left' | 'right' | 'up' | 'down';
  activeIndex?: number;
  loop?: boolean;
  autoPlay?: boolean;
  autoPlayIntervalTime?: number;
  showPagination?: boolean;
  paginationType?: 'fraction' | 'bullets';
  className?: string;
  style?: React.CSSProperties;
  onLog?: (name: string, index?: number) => void
}

interface CarouselState {
  visible: boolean;
  videoUrl: string;
  index: number;
  prevIndex?: number;
}

const root: { [propName: string]: any; } = (typeof window !== 'undefined' ? window : {});

const getIndex = (props: CarouselProps) => {
  if (props && props.activeIndex) {
    return props.activeIndex;
  }

  return 0;
};

class UCarousel extends React.PureComponent<CarouselProps, CarouselState> {
  static propTypes = {
    prefixCls: PropTypes.string,
    onChange: PropTypes.func,
    activeIndex: PropTypes.number,
  };

  static defaultProps = {
    prefixCls: 'crm-carousel',
    activeIndex: 0,
    onChange: () => { },
  };

  state: CarouselState = {
    visible: false,
    videoUrl: '',
    index: getIndex(this.props),
  };

  // 定义播放器
  player: any = null;

  static getDerivedStateFromProps(nextProps: CarouselProps, state: CarouselState) {
    if (typeof nextProps.activeIndex !== 'undefined' && nextProps.activeIndex !== state.prevIndex) {
      return {
        index: getIndex(nextProps),
        prevIndex: nextProps.activeIndex,
      };
    }

    return null;
  }

  componentWillUnmount() {
    // 销毁播放器实例
    this.player && this.player.dispose();
  }

  onChange(index: number) {
    this.setState({ index });
  }

  onClose() {
    // 销毁播放器实例
    this.player && this.player.dispose();

    this.setState({ visible: false });
  }

  onPlayAndPause(item: any, index: number) {
    const { onLog } = this.props;
    const { videoUrl, log } = item || {};

    // 用于埋点标记，回调
    onLog && onLog((log || 'carouselPlayVideo'), index);

    if (!videoUrl) {
      Toast.show('打开视频失败!');
      return false;
    }

    this.setState({
      visible: true,
      videoUrl,
    });
    const that = this;

    this.loaderVideoJS()
      .then(() => {
        if (!root.videojs) {
          return false;
        }

        const options = {
          controls: true,
          // autoplay: true,
          preload: 'auto',
          autoplay: true,
          controlBar: {
            fullscreenToggle: false,
            disablePictureInPicture: false,
          },
          'webkit-playsinline': true,
          webkitPlaysinline: true,
          playsinline: true,
        };

        this.player = root.videojs('J_carouselVideo', options);

        // eslint-disable-next-line func-names
        this.player.ready(function () {
          // 立即播放
          this.play();

          // 监听视频播放完成执行事件
          this.on('ended', () => {
            that.onClose();
          });
        });
      });
  }

  onImage = (item: any, index: number) => {
    const { onLog } = this.props;
    const { log } = item || {};

    // 用于埋点标记，回调
    onLog && onLog(log || 'carousel', index);
  };

  // 加载video-js 静态资源
  loaderVideoJS = (): Promise<any> => {
    return new Promise((resolve) => {
      // 未加载资源
      if (typeof root.videojs === 'undefined') {
        load.css('//static.iyb.tm/web/lib/video-js/video-js.min.css');
        load.js('//static.iyb.tm/web/lib/video-js/video.min.js', () => resolve(null));
        return false;
      }

      resolve(null);
    });
  };

  renderItem(options: OptionsProps) {
    const { prefixCls } = this.props;

    return options.map((item, index) => {
      switch (item.type) {
        case 'video':
          return (
            <div
              className={classnames(`${prefixCls}-item`, `${prefixCls}-video`)}
              // eslint-disable-next-line react/no-array-index-key
              key={`item_${index}`}
              onClick={() => this.onPlayAndPause(item, index)}
            >
              <div className={`${prefixCls}-poster`}>
                <img src={item.href} alt="" />
              </div>
            </div>
          );
        default:
          return (
            <div
              className={`${prefixCls}-item ${prefixCls}-image`}
              // eslint-disable-next-line react/no-array-index-key
              key={`item_${index}`}
            >
              {
                item.url ? <a href={item.url} onClick={() => this.onImage(item, index)}><img src={item.href} alt={item.alt || ''} /></a> : <img src={item.href} alt={item.alt || ''} />
              }
            </div>
          );
      }
    });
  }

  renderPagination(options: OptionsProps) {
    const { index } = this.state;
    const { prefixCls } = this.props;

    return (
      <div className={`${prefixCls}-pagination`}>
        {index + 1}/{options.length}
      </div>
    );
  }

  // 视频层
  renderVideo() {
    const { prefixCls } = this.props;
    const { visible, videoUrl } = this.state;
    const cls = `${prefixCls}-popup`;

    return (
      <LockScroll>
        <Popup
          visible={visible}
          direction="center"
          className={cls}
          onMaskClick={() => this.onClose()}
        >
          <div className={`${cls}-video`}>
            <Button
              shape="circle"
              size="sm"
              className={`${cls}-close`}
              onClick={() => this.onClose()}
            >
              关闭
            </Button>
            <video
              className="video-js vjs-theme-city"
              id="J_carouselVideo"
              preload="preload"
            >
              <source src={videoUrl} type="video/mp4" />
              浏览器不支持
            </video>
          </div>
        </Popup>
      </LockScroll>
    );
  }

  render() {
    const {
      prefixCls,
      direction = 'left',
      options,
      showPagination = true,
      paginationType = 'fraction',
      className,
      autoPlayIntervalTime = 5000,
      ...others
    } = this.props;

    const cls = classnames(prefixCls, className);

    if (!(options && Array.isArray(options) && options.length)) {
      return null;
    }

    return (
      <div className={cls}>
        <Carousel
          {...others}
          autoPlayIntervalTime={autoPlayIntervalTime}
          direction={direction}
          showPagination={showPagination && paginationType === 'bullets'}
          onChange={(activeIndex: number) => this.onChange(activeIndex)}
        >
          {this.renderItem(options)}
        </Carousel>
        {(showPagination && paginationType === 'fraction') ? this.renderPagination(options) : null}
        {this.renderVideo()}
      </div>
    );
  }
}

export default UCarousel;
